/**
 * OneSignal integration (Web + Capacitor native).
 * - In web: lazily loads the OneSignal Web SDK v16.
 * - In native: uses the cordova plugin via `window.cordova.plugins.OneSignal`.
 * Player ids are registered with the backend so admins can target specific users.
 */
import { api } from "@/lib/api";
import { isNative } from "@/lib/native";

let _initPromise = null;
let _appId = null;
let _lastRegisteredId = null;

const fetchAppId = async () => {
  if (_appId) return _appId;
  try {
    const { data } = await api.get("/push/config");
    if (data?.enabled && data?.app_id) _appId = data.app_id;
  } catch {
    /* ignore */
  }
  return _appId;
};

const registerPlayer = async (playerId, platform) => {
  if (!playerId) return;
  if (playerId === _lastRegisteredId) return; // dedupe
  try {
    await api.post("/push/register", { player_id: playerId, platform });
    _lastRegisteredId = playerId;
    // eslint-disable-next-line no-console
    console.info("[OneSignal] player_id registered:", playerId.slice(0, 12) + "...");
  } catch (e) {
    // Silent — will retry on next event
  }
};

const initWeb = async () => {
  const appId = await fetchAppId();
  if (!appId || typeof window === "undefined") return;

  if (!document.getElementById("onesignal-sdk-v16")) {
    await new Promise((resolve) => {
      const s = document.createElement("script");
      s.id = "onesignal-sdk-v16";
      s.src = "https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js";
      s.defer = true;
      s.onload = resolve;
      s.onerror = resolve;
      document.head.appendChild(s);
    });
  }

  window.OneSignalDeferred = window.OneSignalDeferred || [];
  window.OneSignalDeferred.push(async (OneSignal) => {
    try {
      if (!window.__OS_INITIALIZED) {
        await OneSignal.init({
          appId,
          allowLocalhostAsSecureOrigin: true,
          serviceWorkerParam: { scope: "/" },
          serviceWorkerPath: "OneSignalSDKWorker.js",
        });
        window.__OS_INITIALIZED = true;
      }
      const handlePid = async () => {
        try {
          const pid = OneSignal.User?.PushSubscription?.id;
          if (pid) await registerPlayer(pid, "web");
        } catch { /* ignore */ }
      };
      OneSignal.User?.PushSubscription?.addEventListener?.("change", handlePid);
      await handlePid();
    } catch { /* ignore */ }
  });
};

const getNativeOS = () =>
  window?.plugins?.OneSignal || window?.cordova?.plugins?.OneSignal;

const initNative = async () => {
  const appId = await fetchAppId();
  if (!appId) return;
  const OS = getNativeOS();
  if (!OS) {
    // eslint-disable-next-line no-console
    console.warn("[OneSignal] native plugin not found");
    return;
  }
  try {
    OS.initialize?.(appId);
    // Tag the OneSignal subscription with our backend user id (external user id)
    // so we can send pushes by user_id even when the device-level id rotates.
    try {
      const u = JSON.parse(localStorage.getItem("fidelis_user") || "null");
      if (u?.id) OS.login?.(u.id);
    } catch { /* ignore */ }

    try {
      OS.Notifications?.requestPermission?.(true);
    } catch { /* ignore */ }
    try {
      OS.User?.pushSubscription?.optIn?.();
    } catch { /* ignore */ }

    const tryGetId = () => {
      try {
        const sub = OS.User?.pushSubscription;
        const pid = sub?.id || sub?.getId?.();
        const platform = /iPhone|iPad|iPod/i.test(navigator.userAgent) ? "ios" : "android";
        if (pid) registerPlayer(pid, platform);
      } catch { /* ignore */ }
    };
    // FCM token can take a few seconds; retry several times
    setTimeout(tryGetId, 1500);
    setTimeout(tryGetId, 4000);
    setTimeout(tryGetId, 8000);
    setTimeout(tryGetId, 15000);
    OS.User?.pushSubscription?.addEventListener?.("change", tryGetId);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[OneSignal] init error", e);
  }
};

export const initOneSignal = () => {
  if (_initPromise) return _initPromise;
  _initPromise = (async () => {
    if (isNative()) await initNative();
    else await initWeb();
  })();
  return _initPromise;
};

/**
 * Link the current OneSignal subscription to our backend user id.
 * MUST be called right after a successful login — that's how OneSignal
 * knows which device belongs to which user (used for `include_external_user_ids`).
 */
export const onesignalLogin = async (userId) => {
  if (!userId) return;
  await initOneSignal();
  if (isNative()) {
    const OS = getNativeOS();
    if (!OS) return;
    try {
      OS.login?.(String(userId));
      // Some plugin versions expose addAlias instead/also
      OS.User?.addAlias?.("external_id", String(userId));
      // eslint-disable-next-line no-console
      console.info("[OneSignal] native login as", userId);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[OneSignal] login error", e);
    }
  } else {
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    window.OneSignalDeferred.push(async (OneSignal) => {
      try {
        await OneSignal.login?.(String(userId));
        // eslint-disable-next-line no-console
        console.info("[OneSignal] web login as", userId);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[OneSignal] web login error", e);
      }
    });
  }
};

/** Logs the user out of OneSignal — call this on app logout. */
export const onesignalLogout = async () => {
  if (isNative()) {
    const OS = getNativeOS();
    try { OS?.logout?.(); } catch { /* ignore */ }
  } else {
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    window.OneSignalDeferred.push((OneSignal) => {
      try { OneSignal.logout?.(); } catch { /* ignore */ }
    });
  }
  _lastRegisteredId = null;
};

/**
 * Force-refresh the subscription. Use when the admin reports the user isn't
 * receiving pushes — likely the cached player_id became invalid.
 */
export const refreshSubscription = async () => {
  _lastRegisteredId = null;
  if (isNative()) {
    const OS = getNativeOS();
    if (!OS) return { ok: false, reason: "plugin_not_found" };
    try {
      OS.Notifications?.requestPermission?.(true);
      OS.User?.pushSubscription?.optIn?.();
      // Wait a bit for FCM to refresh
      await new Promise((r) => setTimeout(r, 3000));
      const pid = OS.User?.pushSubscription?.id;
      const platform = /iPhone|iPad|iPod/i.test(navigator.userAgent) ? "ios" : "android";
      if (pid) {
        await registerPlayer(pid, platform);
        return { ok: true, player_id: pid };
      }
      return { ok: false, reason: "no_player_id" };
    } catch (e) {
      return { ok: false, reason: String(e) };
    }
  } else {
    await initWeb();
    return new Promise((resolve) => {
      window.OneSignalDeferred = window.OneSignalDeferred || [];
      window.OneSignalDeferred.push(async (OneSignal) => {
        try {
          // Tag external user id so we can target by user_id regardless of sub id rotation
          try {
            const u = JSON.parse(localStorage.getItem("fidelis_user") || "null");
            if (u?.id) await OneSignal.login?.(u.id);
          } catch { /* ignore */ }

          await OneSignal.Slidedown?.promptPush?.({ force: true });
          const opted = OneSignal.User?.PushSubscription?.optedIn;
          if (!opted) await OneSignal.User?.PushSubscription?.optIn?.();
          const pid = OneSignal.User?.PushSubscription?.id;
          if (pid) {
            await registerPlayer(pid, "web");
            resolve({ ok: true, player_id: pid });
          } else {
            resolve({ ok: false, reason: "no_player_id" });
          }
        } catch (e) {
          resolve({ ok: false, reason: String(e) });
        }
      });
    });
  }
};

/** Returns the current subscription state for debugging UI. */
export const getSubscriptionStatus = async () => {
  if (isNative()) {
    const OS = getNativeOS();
    if (!OS) return { available: false, reason: "Plugin OneSignal não encontrado (precisa rebuildar o APK)" };
    try {
      const sub = OS.User?.pushSubscription;
      let externalId = null;
      try { externalId = OS.User?.getExternalId?.() || OS.User?.externalId || null; } catch { /* ignore */ }
      return {
        available: true,
        player_id: sub?.id || null,
        external_id: externalId,
        opted_in: sub?.optedIn ?? null,
        platform: /iPhone|iPad|iPod/i.test(navigator.userAgent) ? "ios" : "android",
      };
    } catch (e) {
      return { available: false, reason: String(e) };
    }
  }
  return new Promise((resolve) => {
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    window.OneSignalDeferred.push((OneSignal) => {
      try {
        resolve({
          available: true,
          player_id: OneSignal.User?.PushSubscription?.id || null,
          external_id: OneSignal.User?.externalId || null,
          opted_in: OneSignal.User?.PushSubscription?.optedIn ?? null,
          platform: "web",
          notification_permission: typeof Notification !== "undefined" ? Notification.permission : "unsupported",
        });
      } catch (e) {
        resolve({ available: false, reason: String(e) });
      }
    });
  });
};

export const requestPushAndRegister = refreshSubscription;
